import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, RefreshCw, Trash2, Play, Loader2, AlertCircle, Mail, FileSearch } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/prospect-audits`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '');
const RAG = { Critical: 'bg-red-700', High: 'bg-[#BC516F]', Medium: 'bg-amber-600', Low: 'bg-emerald-700', Good: 'bg-emerald-700' };

const NewAuditForm = ({ onCreated }) => {
  const [form, setForm] = useState({ website: '', prospect_name: '', podcast: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(API, form, { headers: authHeaders() });
      toast.success('Audit started. It runs in the background, so you can leave this page.');
      setForm({ website: '', prospect_name: '', podcast: '' });
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start the audit');
    } finally {
      setBusy(false);
    }
  };
  const field = 'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#A7468A]/30 focus:border-[#A7468A]';
  return (
    <form onSubmit={submit} className="bg-white rounded-xl border border-gray-200 p-5 grid gap-3 md:grid-cols-[1.2fr_1fr_1.4fr_auto] items-end" data-testid="audit-new-form">
      <label className="text-xs font-semibold text-gray-600">Website *
        <input required value={form.website} onChange={set('website')} placeholder="dryip.com" className={`${field} mt-1`} data-testid="audit-website-input" />
      </label>
      <label className="text-xs font-semibold text-gray-600">Prospect name
        <input value={form.prospect_name} onChange={set('prospect_name')} placeholder="Dr. Jenny Yip" className={`${field} mt-1`} data-testid="audit-name-input" />
      </label>
      <label className="text-xs font-semibold text-gray-600">Podcast (optional: name, Apple link or RSS)
        <input value={form.podcast} onChange={set('podcast')} placeholder="Auto-detected from the site if blank" className={`${field} mt-1`} data-testid="audit-podcast-input" />
      </label>
      <button disabled={busy} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#A7468A] hover:bg-[#BC516F] text-white text-sm font-semibold transition-colors disabled:opacity-60" data-testid="audit-run-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run audit
      </button>
    </form>
  );
};

const Progress = ({ a }) => {
  const pct = a.pages_total ? Math.round((a.pages_done / a.pages_total) * 100) : 0;
  const crawling = a.stage === 'crawling' && a.pages_total;
  const minsLeft = crawling ? Math.ceil(((a.pages_total - a.pages_done) * ((a.crawl_delay || 1.5) + 0.7)) / 60) : null;
  return (
    <div className="space-y-1.5" data-testid={`audit-progress-${a.id}`}>
      <div className="flex items-center gap-2 text-sm text-gray-700"><Loader2 className="w-3.5 h-3.5 animate-spin text-[#A7468A]" /> {a.stage_label}</div>
      {crawling ? (
        <>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-[#A7468A] transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
          <p className="text-xs text-gray-500">{a.pages_done} / {a.pages_total} pages · about {minsLeft} min left in the crawl</p>
        </>
      ) : null}
    </div>
  );
};

const Scores = ({ counts }) => (
  <div className="flex flex-wrap gap-1.5">
    {['Critical', 'High', 'Medium', 'Low', 'Good'].filter((k) => counts?.[k]).map((k) => (
      <span key={k} className={`px-2 py-0.5 rounded-full text-[11px] font-semibold text-white ${RAG[k]}`}>{counts[k]} {k}</span>
    ))}
  </div>
);

const AuditRow = ({ a, onDownload, onRebuild, onDelete }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 grid gap-4 md:grid-cols-[1.3fr_1.7fr_auto] items-start" data-testid={`audit-row-${a.id}`}>
    <div className="min-w-0">
      <p className="font-semibold text-gray-900 truncate" data-testid={`audit-name-${a.id}`}>{a.display_name || a.prospect_name || a.website}</p>
      <a href={a.origin || `https://${a.website}`} target="_blank" rel="noreferrer" className="text-sm text-[#A7468A] hover:underline break-all">{(a.origin || a.website).replace(/^https?:\/\//, '')}</a>
      <p className="text-xs text-gray-500 mt-1">{fmtDate(a.created_at)}{a.platform && a.platform !== 'Unknown' ? ` · ${a.platform}` : ''}{a.pages_total ? ` · ${a.pages_total} page${a.pages_total === 1 ? '' : 's'}` : ''}</p>
    </div>
    <div className="min-w-0">
      {a.status === 'done' && (
        <div className="space-y-2">
          <Scores counts={a.score_counts} />
          {a.headline && <p className="text-sm text-gray-600 line-clamp-2">{a.headline}</p>}
          <p className="text-xs text-gray-500 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {a.email_sent ? 'Emailed to jo@audioandco.com' : 'Email not sent (check the Brevo authorised IPs)'}</p>
        </div>
      )}
      {(a.status === 'running' || a.status === 'queued') && <Progress a={a} />}
      {a.status === 'error' && <p className="text-sm text-red-700 inline-flex items-start gap-1.5" data-testid={`audit-error-${a.id}`}><AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {a.error}</p>}
    </div>
    <div className="flex md:flex-col gap-2 md:items-end">
      {a.status === 'done' && (
        <button onClick={() => onDownload(a)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#A7468A] hover:bg-[#BC516F] text-white text-sm font-semibold transition-colors" data-testid={`audit-download-${a.id}`}>
          <Download className="w-4 h-4" /> Word report
        </button>
      )}
      {(a.status === 'done' || a.status === 'error') && (
        <button onClick={() => onRebuild(a)} title={a.status === 'error' ? 'Resume from where it stopped' : 'Rewrite the copy and rebuild the document'} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm transition-colors" data-testid={`audit-rebuild-${a.id}`}>
          <RefreshCw className="w-4 h-4" /> {a.status === 'error' ? 'Resume' : 'Rebuild'}
        </button>
      )}
      {a.status !== 'running' && (
        <button onClick={() => onDelete(a)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-gray-500 hover:text-red-700 hover:bg-red-50 text-sm transition-colors" data-testid={`audit-delete-${a.id}`}>
          <Trash2 className="w-4 h-4" /> Delete
        </button>
      )}
    </div>
  </div>
);

export default function AuditsPanel() {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(API, { headers: authHeaders() });
      setAudits(data.audits);
    } catch (err) {
      toast.error('Could not load audits');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const active = audits.some((a) => a.status === 'running' || a.status === 'queued');
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [active, load]);

  const download = async (a) => {
    try {
      const res = await axios.get(`${API}/${a.id}/download`, { headers: authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url; link.download = a.filename || 'seo-audit.docx'; link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };
  const rebuild = async (a) => {
    try { await axios.post(`${API}/${a.id}/rebuild`, {}, { headers: authHeaders() }); toast.success('Rebuilding the report'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not rebuild'); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete the audit for ${a.display_name || a.website}?`)) return;
    try { await axios.delete(`${API}/${a.id}`, { headers: authHeaders() }); setAudits((x) => x.filter((y) => y.id !== a.id)); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not delete'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-audits-tab">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Prospect Audits</h2>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">Crawls every page in the sitemap, checks the podcast on Apple, Spotify and RSS, then writes a branded Word report. Each finished report is also emailed to jo@audioandco.com.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900" data-testid="audit-refresh-btn"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>
      <NewAuditForm onCreated={load} />
      {loading ? <div className="text-sm text-gray-500">Loading…</div> : audits.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center" data-testid="audit-empty">
          <FileSearch className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No audits yet. Enter a prospect's website above to run the first one.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="audit-list">
          {audits.map((a) => <AuditRow key={a.id} a={a} onDownload={download} onRebuild={rebuild} onDelete={remove} />)}
        </div>
      )}
    </div>
  );
}
